<template>
  <base-wrapper :loading="loading">
    <div class="space-y-4">
      <div v-if="filter.isOmzet" class="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-6">
        <BaseWidget label="Omzet" :value="orders.meta.total_price" />
      </div>
      <div class="flex items-center justify-end gap-2">
        <BaseCheckbox label="Omzet" name="omzet" v-model="filter.isOmzet" @change="loadOrders" />
        <base-checkbox v-if="!filter.isOmzet" name="area" label="Harga Tanpa Kebijakan" v-model="filter.displayDefaultAreaPrice" />
        <period-select
          class="gap-4"
          v-model="filter.periodId"
          @change="loadOrders"
        />
        <base-select
          v-if="!filter.isOmzet"
          class="w-48"
          :shadow="false"
          :options="filterOrderStatusOptions"
          v-model="filter.orderStatus"
          @change="loadOrders"
        />
        <base-input
          type="search"
          :shadow="false"
          placeholder="Cari Kode atau Nama"
          debounce
          v-model="filter.search"
          @native-input="loadOrders"
        />
        <base-button :to="{ name: 'penjualan.tambah' }">
          <Icon icon="heroicons:plus-solid" class="h-4 w-4" />
          Tambah Transaksi
        </base-button>
      </div>

      <datatable
        :columns="tableColumns"
        :total="orders.meta.page.total"
        :perPage="orders.meta.page.perPage"
        :currentPage="orders.meta.page.currentPage"
        :meta="orders.meta"
        @pagechanged="onChangePage"
      >
        <template #tbody="{ classes }">
          <tr
            v-for="order in orders.data"
            :key="order.id"
            :class="[classes.tr, getRowClass(order), 'cursor-pointer hover:bg-green-100']"
            @click="onClickDetail(order.attributes.order_id)"
          >
            <td :class="[classes.td, 'space-y-1']">
              <p class="font-bold text-gray-900">
                {{ order.attributes.origin_code }}
              </p>
              <p class="text-xs text-gray-500">
                {{ order.attributes.updatedAt | formatDate }}
              </p>
            </td>
            <td :class="[classes.td]">
              {{
                getSingleIncluded(
                  orders,
                  order.relationships['destination-office'].data.id
                ).attributes.code
              }}
              /
              {{
                getSingleIncluded(
                  orders,
                  order.relationships['origin-office'].data.id
                ).attributes.code
              }}
            </td>
            <td :class="[classes.td, 'text-right']">
              {{
                getSingleIncluded(orders, order.relationships['order'].data.id)
                  .attributes[filter.displayDefaultAreaPrice ? 'default_area_price' : 'grand_total_price'] | toCurrency
              }}
            </td>
            <td
              :class="[classes.td, 'text-center']"
              :title="
                getSingleIncluded(orders, order.relationships['order'].data.id)
                  .attributes.is_valid_for_packing &&
                !getSingleIncluded(orders, order.relationships['order'].data.id)
                  .attributes.is_paid
                  ? 'Menunggu Verifikasi EDC'
                  : ''
              "
            >
              <base-badge
                v-if="order.attributes.order_status === 'Terlaporkan'"
                color="green"
                :icon="
                  getSingleIncluded(
                    orders,
                    order.relationships['order'].data.id
                  ).attributes.is_valid_for_packing &&
                  !getSingleIncluded(
                    orders,
                    order.relationships['order'].data.id
                  ).attributes.is_paid
                    ? 'heroicons:exclamation-triangle-solid'
                    : ''
                "
                >Selesai</base-badge
              >
              <base-badge
                v-else
                color="yellow"
                :icon="
                  getSingleIncluded(
                    orders,
                    order.relationships['order'].data.id
                  ).attributes.is_valid_for_packing &&
                  !getSingleIncluded(
                    orders,
                    order.relationships['order'].data.id
                  ).attributes.is_paid
                    ? 'heroicons:exclamation-triangle-solid'
                    : ''
                "
                >Belum Selesai</base-badge
              >
            </td>
            <td :class="[classes.td, 'text-center']">
              <base-badge
                v-if="order.attributes.order_status === 'Draft'"
                color="yellow"
                >Draft</base-badge
              >
              <base-badge v-else color="green">Valid</base-badge>
            </td>
            <td :class="[classes.td, 'text-center']" :title="getSingleIncluded(
              orders,
              order.relationships['order'].data.id
            ).attributes.latest_cancel_description">
              <base-badge
                v-if="order.attributes.order_status === 'Draft'"
                color="yellow"
                >Draft</base-badge
              >
              <base-badge v-else color="indigo">{{ order.attributes.order_status }}</base-badge>
            </td>
            <td :class="[classes.td]">
              <p v-if="order.relationships['period'].data">
                {{
                  getSingleIncluded(
                    orders,
                    order.relationships['period'].data.id
                  ).attributes.start_date | formatDate('MMYY')
                }}
              </p>
              <p v-else>-</p>
            </td>
          </tr>
        </template>
      </datatable>
    </div>

    <view-order-modal
      label="Penjualan"
      type="sale"
      with-bonus
      edit-route-name="penjualan.edit"
      :order-detail-columns="['code', 'qty', 'price', 'total_price', 'packed_qty', 'unpacked_qty']"
      :columns="[
        'code',
        'warehouse',
        'office',
        'date',
        'user',
        'area',
        'shipment_type',
        'address',
      ]"
      :visible="detailModal.visible"
      :order-id="detailModal.orderId"
      :display-default-area-price="filter.displayDefaultAreaPrice"
      @close="detailModal.visible = false"
      @validated="onRefresh"
      @deleted="onRefresh"
    />
  </base-wrapper>
</template>

<script>
import { requestMixin } from '@/mixins/request/request';
import { mapGetters } from 'vuex';
import ViewOrderModal from '@/components/order/view-order-modal.vue';
import BaseCheckbox from '../../../../components/base/BaseCheckbox.vue';
import PeriodSelect from '../../../../components/period/period-select.vue';
import BaseWidget from '../../../../components/base/BaseWidget.vue';

export default {
  mixins: [requestMixin],
  components: {
    ViewOrderModal,
    BaseCheckbox,
    PeriodSelect,
    BaseWidget
  },
  data() {
    return {
      filter: {
        search: null,
        periodId: null,
        displayDefaultAreaPrice: false,
        orderStatus: null,
        isOmzet: false
      },
      detailModal: {
        orderId: null,
        visible: false,
      },
      loading: true,
      orders: {
        data: [],
        meta: {
          page: {},
        },
      },
    };
  },
  computed: {
    ...mapGetters({
      me: 'auth/getUser',
    }),
    tableColumns() {
      return [
        { id: 'code', name: 'Nomor Faktur' },
        { id: 'office_code', name: 'Kode Gudang | Kode Stockist' },
        {
          id: 'final_price',
          name: 'Total Harga',
          theadClass: 'text-right',
        },
        {
          id: 'report_status',
          name: 'Status Laporan',
          theadClass: 'text-center',
        },
        {
          id: 'order_status',
          name: 'Status Faktur',
          theadClass: 'text-center',
        },
        {
          id: 'warehouse_status',
          name: 'Status Pengiriman',
          theadClass: 'text-center',
        },
        {
          id: 'period',
          name: 'PERIODE',
        },
      ];
    },
    filterOrderStatusOptions() {
      return [
        {
          key: 'null',
          label: 'Semua',
          value: null,
        },
        {
          key: 'valid',
          label: 'Valid',
          value: 'valid',
        },
        {
          key: 'draft',
          label: 'Draft',
          value: 'draft',
        },
        {
          key: 'cancel',
          label: 'Batal Faktur',
          value: 'cancel',
        },
        {
          key: 'hapus',
          label: 'Hapus',
          value: 'hapus',
        },
      ];
    },
    filterOrderStatus() {
      if (this.filter.isOmzet) {
        return {}
      }
      
      if (
        this.filter.orderStatus === null ||
        this.filter.orderStatus === 'hapus'
      ) {
        return null;
      }

      if (this.filter.orderStatus === 'valid') {
        return {
          'filter[not-order_status]':
            'Draft,Batal Faktur Gudang,Batal Faktur Kasir,Terhapus',
        };
      }

      if (this.filter.orderStatus === 'draft') {
        return {
          'filter[order_status]': 'Draft',
        };
      }

      return {
        'filter[order_status]': 'Batal Faktur Gudang,Batal Faktur Kasir',
      };
    },
  },
  methods: {
    getRowClass(simpleOrder) {
      const order = this.getSingleIncluded(
        this.orders,
        simpleOrder.relationships['order'].data.id
      )

      if (!order.attributes.default_area_price) {
        return 'bg-white'
      }

      if (order.attributes.default_area_price === order.attributes.grand_total_price) {
        return 'bg-white'
      }

      return 'bg-yellow-50'
    },
    async loadOrders(params) {
      this.loading = true;

      const [res, err] = await this.request('/api/v1/orders', {
        params: {
          'page[size]': 5,
          include: 'order,origin-office,destination-office,period',
          'fields[simple-orders]':
            'order,period,origin_code,updatedAt,order_status,order_id,warehouse_status,origin-office,destination-office',
          'fields[offices]': 'code',
          'fields[orders]': 'default_area_price,grand_total_price,is_paid,is_valid_for_packing,latest_cancel_description',
          'fields[periods]': 'start_date',
          'filter[destination_office_category_id]': 4,
          'filter[origin_warehouse_id]': this.me.current_warehouse,
          'filter[search]': this.filter.search,
          [`filter[${this.filter.isOmzet ? 'kp_omzet_period_uuid' : 'period_uuid'}]`]: this.filter.periodId,
          'filter[with_summary]': true,
          'filter[is_all_transfer_promo]': false,
          ...this.filterOrderStatus,
          ...params,
        },
      });

      if (!err) {
        this.orders = res;
      }

      this.loading = false;
    },
    async loadCurrentPeriod() {
      const [res, err] = await this.request(
        '/api/v1/periods/-actions/get-current-period'
      );

      if (!err) {
        this.filter.periodId = res.data.id;
      }
    },
    onChangePage(page) {
      this.loadOrders({
        'page[number]': page,
      });
    },
    onClickDetail(orderId) {
      this.detailModal.orderId = orderId;
      this.detailModal.visible = true;
    },
    onRefresh() {
      this.detailModal.visible = false;

      this.loadOrders();
    },
  },
  async created() {
    await this.loadCurrentPeriod()

    this.loadOrders();
  },
};
</script>
